// Fonctions PURES de l'écran « Supprimer intégralement ce site » (hosting-migration B2_09).
// Tout ce qui décide d'un LIBELLÉ vit ici, testé sans rendu (D38 : pas de dépendance de test
// d'écran) — gabarit src/lib/websiteActivity.js.
//
// Règles non négociables de cet écran :
//  - un comptage qui n'a pas abouti ne s'affiche JAMAIS « 0 » ;
//  - la progression n'affiche JAMAIS de pourcentage : des bornes BASSES assumées
//    (« reste ≥ M », « fin estimée ≥ T ») ;
//  - un champ ABSENT de la réponse (API plus ancienne) n'est pas un état métier : « — ».

// U+202F : espace fine insécable, celle de toLocaleString('fr-FR') (piège de recette connu).
export const formatNumber = (n) =>
  typeof n === 'number' && Number.isFinite(n) ? n.toLocaleString('fr-FR') : '—';

// --- Inventaire ---------------------------------------------------------------------------------

export function countLabel(count) {
  if (!count || typeof count !== 'object') return '—';
  switch (count.state) {
    case 'exact':
      return formatNumber(count.value);
    case 'at_least':
      return `plus de ${formatNumber(count.value)} (comptage interrompu)`;
    case 'interrupted':
      return count.reason === 'time_budget' ? 'non compté (délai de l’écran dépassé)' : 'comptage interrompu';
    case 'not_counted_no_index':
      return count.purgeable_without_index === false ? 'non compté (pas d’index)' : 'non compté';
    case 'error':
      return 'erreur de comptage';
    default:
      return '—';
  }
}

const ACTION_LABELS = {
  delete: 'supprimé',
  cascade: 'supprimé en partie (par commentaire)',
  keep: 'conservé',
  reference_kept: 'conservé (simple mention du site)',
  skip_no_index: 'non supprimé (collection sans index)',
  blocked: 'BLOQUANT — non classé',
};
export const actionLabel = (action) => ACTION_LABELS[action] || action || '—';

// Une ligne mérite l'affichage si elle porte des documents, ou si on ne SAIT pas (non compté).
export const isWorthShowing = (entry) =>
  Boolean(entry && entry.count && (entry.count.value === null || entry.count.value > 0 || entry.action === 'blocked'));

export function splitInventory(inventory) {
  const rows = (inventory && Array.isArray(inventory.collections) && inventory.collections) || [];
  const shown = rows.filter(isWorthShowing);
  const deleted = shown.filter((e) => e.action === 'delete' || e.action === 'cascade');
  const kept = shown.filter((e) => e.action !== 'delete' && e.action !== 'cascade');
  const byValue = (a, b) => (b.count.value || 0) - (a.count.value || 0);
  return { deleted: deleted.sort(byValue), kept: kept.sort(byValue), hiddenEmpty: rows.length - shown.length };
}

export function totalLabel(totals) {
  if (!totals || typeof totals.documents_to_delete !== 'number') return '—';
  const n = formatNumber(totals.documents_to_delete);
  return totals.documents_to_delete_is_lower_bound ? `au moins ${n} documents` : `${n} documents`;
}

const BLOCKER_LABELS = {
  unclassified_path: 'un lien vers le site n’est pas classé (à ranger côté API avant toute suppression)',
  collections_unreadable: 'les collections ne sont pas lisibles (droits de la base)',
  guard_inactive_in_production: 'la surveillance de la réplication n’est pas active',
  guard_unavailable_in_production: 'la surveillance de la réplication est indisponible',
};
export const blockerLabel = (b) => (b && (BLOCKER_LABELS[b.code] || b.msg || b.code)) || '—';

// Refus métier de l'API (`code`) en clair. Inconnu → le message du serveur.
const REFUSAL_LABELS = {
  purge_already_running: 'Une suppression est déjà en cours — une seule à la fois, tous sites confondus.',
  active_subscription: 'Ce site a un abonnement Stripe actif : annule le plan d’abord.',
  blocked: 'Suppression impossible pour ce site.',
  inventory_incomplete: 'L’inventaire n’a pas eu le temps de tout vérifier : réessaie dans un instant.',
  stripe_unavailable: 'Stripe est injoignable : impossible de vérifier l’abonnement, rien n’a été lancé.',
  publish_failed: 'La suppression n’a pas pu être mise en file : rien n’a été lancé, le site est inchangé.',
  website_not_found: 'Ce site n’existe pas (ou plus).',
  wrong_rate: 'Débit refusé par le serveur.',
};
export const refusalLabel = (error) =>
  (error && (REFUSAL_LABELS[error.code] || error.msg || error.code)) || 'erreur inconnue';

// « La place est tenue par … » — sans ça, la seule issue serait une lecture de la base.
export function activeJobLabel(activeJob) {
  if (!activeJob || typeof activeJob !== 'object') return null;
  const name = (activeJob.website && (activeJob.website.name || activeJob.website.url)) || activeJob.website_id || '—';
  return `Place tenue par : ${name} (${activeJob.website_id || '—'}) — ${statusLabel(activeJob.status)}`;
}

// --- Confirmation ----------------------------------------------------------------------------------

// Confirmation par SAISIE du nom du site (irréversible). Un site sans nom se confirme par son id.
export const expectedConfirmation = (website) =>
  (website && typeof website.name === 'string' && website.name.trim()) || (website && website._id) || '';

export const canConfirm = (typed, website) => {
  const expected = expectedConfirmation(website);
  return Boolean(expected) && typeof typed === 'string' && typed.trim() === expected;
};

// --- Suivi -------------------------------------------------------------------------------------------

const STATUS_LABELS = {
  queued: 'en attente de démarrage',
  running: 'suppression en cours',
  paused: 'en pause (reprend seule)',
  stopped: 'arrêtée par toi',
  interrupted: 'interrompue par une erreur',
  done: 'terminée',
  done_with_leftovers: 'terminée — avec des éléments conservés',
  failed: 'échec au lancement',
};
export const statusLabel = (status) => STATUS_LABELS[status] || (status ? String(status) : '—');

const PAUSE_LABELS = {
  dump_window: 'sauvegarde nocturne de la base (00:00-08:30 UTC)',
  lag_above_threshold: 'la base a du retard de réplication',
  guard_inactive_in_production: 'surveillance de la réplication inactive',
  guard_unavailable_in_production: 'surveillance de la réplication indisponible',
};
export const pauseLabel = (reason) => (reason ? PAUSE_LABELS[reason] || reason : null);

export const isTerminal = (status) => ['done', 'done_with_leftovers', 'failed'].includes(status);
export const isLive = (status) => ['queued', 'running', 'paused'].includes(status);

export function formatDuration(seconds) {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return null;
  if (seconds < 90) return `${Math.round(seconds)} s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} j`;
}

// « N supprimés · reste ≥ M · rythme R/s · fin estimée ≥ T » — jamais un pourcentage.
export function progressLabel(progress) {
  if (!progress || typeof progress !== 'object') return '—';
  const parts = [`${formatNumber(progress.deleted)} supprimés`];
  if (typeof progress.remaining_at_least === 'number' && progress.remaining_at_least > 0) {
    parts.push(`reste ${progress.remaining_is_lower_bound ? '≥ ' : ''}${formatNumber(progress.remaining_at_least)}`);
  } else if (progress.remaining_is_lower_bound) {
    parts.push('reste : inconnu');
  }
  if (typeof progress.observed_docs_per_second === 'number') {
    parts.push(`rythme ${formatNumber(progress.observed_docs_per_second)}/s`);
  }
  const eta = formatDuration(progress.eta_at_least_seconds);
  if (eta) parts.push(`fin estimée ≥ ${eta}`);
  return parts.join(' · ');
}

const LEFTOVER_REASONS = {
  kept_billing: 'conservé — facturation',
  kept_anonymisation_archive: 'conservé — archive d’anonymisation',
  reference_kept: 'conservé — simple mention du site',
  cascade_partial: 'supprimé en partie — les lignes dont le commentaire avait déjà disparu restent',
  skip_no_index: 'non supprimé — collection sans index',
  cascade_skipped_no_index: 'non supprimé — index manquant',
  no_progress: 'non supprimé — la base n’a rien rendu',
  search_index_unavailable: 'moteur de recherche indisponible — balayage non fait',
  kept_referenced_by_anonymisation_archives: 'conservés — une partie est référencée par les archives d’anonymisation',
};
export function leftoverLabel(leftover) {
  if (!leftover) return '—';
  if (leftover.label) return leftover.label;
  const reason = LEFTOVER_REASONS[leftover.reason] || leftover.reason || '—';
  const count = leftover.count ? ` (${countLabel(leftover.count)})` : '';
  return `${leftover.key}${count} : ${reason}`;
}

// `es_sweep_deleted` : 0 = tout a été propagé au fil de l'eau ; > 0 = le filet a servi (à regarder) ;
// null = balayage non fait ; ABSENT = API plus ancienne.
export function searchSweepLabel(value) {
  if (value === undefined) return { state: 'unknown', label: '—' };
  if (value === null) return { state: 'pending', label: 'balayage du moteur de recherche : pas encore fait' };
  if (value === 0) return { state: 'clean', label: 'moteur de recherche : rien ne restait à nettoyer' };
  return { state: 'swept', label: `moteur de recherche : ${formatNumber(value)} documents rattrapés par le balayage final (à signaler)` };
}

// Débits proposés à l'écran (documents par seconde). 250 = premier gros site.
export const RATE_CHOICES = [
  { value: 250, label: '250/s — prudent (premier gros site)' },
  { value: 1000, label: '1 000/s — normal' },
  { value: 2500, label: '2 500/s — rapide (si la base suit)' },
];
export const DEFAULT_RATE = 1000;

// Débit à la REPRISE d'une suppression arrêtée ou interrompue. Le premier choix (valeur '') est
// « inchangé » et reste le défaut : reprendre sans y toucher ne doit JAMAIS faire repartir à
// 1 000/s un site volontairement lancé à 250/s. La liste n'est JAMAIS filtrée : retirer le débit
// courant rendrait la valeur choisie hors-liste dès qu'un rafraîchissement l'égale (champ vidé par MUI).
// `currentRate` absent (API plus ancienne) → « inchangé » sans chiffre, jamais un débit inventé.
export const KEEP_RATE = '';
export function resumeRateChoices(currentRate) {
  const known = typeof currentRate === 'number' && Number.isFinite(currentRate);
  const keep = { value: KEEP_RATE, label: known ? `inchangé — ${formatNumber(currentRate)}/s` : 'inchangé' };
  return [keep, ...RATE_CHOICES];
}
// Ce qu'on envoie à l'API : rien (undefined) tant que « inchangé » est choisi.
export const resumeRateArg = (value) => (value === KEEP_RATE || value === undefined || value === null ? undefined : Number(value));
